import React, { useContext } from "react";
import { AavoButton } from "./AavoButton";
import type { AavoButtonProps } from "./AavoButton";
import { useErrorDialog } from "src/components/common/dialogs/errorDialog/ErrorDialogContext.tsx";
import { AavoMenuItem } from "src/components/common/contextMenu/AavoMenuItem.tsx";
import { AavoContextMenuClosingContext } from "src/components/common/contextMenu/AavoContextMenuClosingContext.ts";
import { useMaybeControlledState } from "src/utils/useMaybeControlledState.ts";

export interface AsyncButtonProps extends Omit<AavoButtonProps, "loading" | "onClick" | "variant"> {
	onClick?: (e: React.MouseEvent<HTMLButtonElement>) => unknown | Promise<unknown>;
	variant?: AavoButtonProps["variant"] | "menu";
	noErrorDialog?: boolean;
	isLoading?: boolean;
	setIsLoading?: (isLoading: boolean) => void;
}

export const AsyncButton = (props: AsyncButtonProps) => {
	const {
		onClick,
		variant,
		noErrorDialog = false,
		isLoading: isLoadingProp,
		setIsLoading: setIsLoadingProp,
		...other
	} = props;
	const [loading, setLoading] = useMaybeControlledState({
		controlledValue: isLoadingProp,
		onChange: (value) => setIsLoadingProp?.(value),
		defaultValue: false,
	});
	const { logErrorAndShowOnDialog } = useErrorDialog();

	const contextMenuClosingContext = useContext(AavoContextMenuClosingContext);
	if (variant === "menu")
		return (
			<AavoMenuItem>
				<AsyncButton
					{...props}
					color={"black"}
					variant={"text"}
					onClick={async (event) => {
						if (onClick) {
							await onClick(event);
						}
						contextMenuClosingContext?.closeContextMenu?.();
					}}
				/>
			</AavoMenuItem>
		);

	return (
		<AavoButton
			variant={variant}
			onClick={async (e) => {
				if (onClick === undefined) return;
				setLoading(true);
				try {
					await onClick(e);
				} catch (e) {
					if (!noErrorDialog) {
						logErrorAndShowOnDialog(e);
					}
				} finally {
					setLoading(false);
				}
			}}
			loading={loading}
			{...other}
		/>
	);
};
